import { useEffect, useState } from 'react';
import { useLoginCustomer } from 'hooks';
import { Icon, SiteLink } from 'components/shared';
import { getResourceImage, getResourceText } from 'lib/resources';
import Button from 'components/shared/button';
import { useSession, signIn } from 'next-auth/react';
import getMultipassToken from 'hooks/customer/useMultipassToken';
import { Spinner } from 'components/shared/icons/ui';
import { PREFIX as accountSlug } from '../dashboard/slugs';
import AccountWrapper from './account-wrapper';

const Login = ({
  page: {
    resources: { access, global },
  },
  localeData: { currency },
}) => {
  const tGlobal = getResourceText(global);
  const t = getResourceText(access);

  const [loginCustomer, { loading: loginLoading, error }] = useLoginCustomer({ currency });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { data: session, status } = useSession();

  const submit = async e => {
    e.preventDefault();
    await loginCustomer({ email, password });
  };

  const loading = (status === 'loading' || status === 'authenticated') ?? loginLoading;

  useEffect(() => {
    const loginWithMultiPass = async () => {
      // get email
      const oAuthEmail = session.user.email;
      // get multipass token
      const multipassToken = await getMultipassToken(oAuthEmail, currency);
      // call log in by passing multipassToken
      await loginCustomer({ multipassToken, isMultipass: true });
    };

    if (session?.user?.email) {
      loginWithMultiPass();
    }
  }, [session?.user?.email, currency]);

  return (
    <AccountWrapper
      loadingOverlay={!!(status === 'loading' || status === 'authenticated')}
      backgroundImage={getResourceImage(access)('Background image')}
    >
      <form className="w-full" onSubmit={e => submit(e)}>
        <div className="mb-7 flex flex-col gap-4 md:mb-9">
          <h2 className="font-nimbus-sans-extd-d text-[2.5rem] font-bold uppercase leading-[85%] tracking-[-0.02em] text-blue">
            {t('Login title', 'Login')}
          </h2>
          <p className="text-body-1 font-nimbus-sans-d text-blue">
            {t('Login Header Description')}
          </p>
        </div>

        <div className="mb-4 flex flex-col gap-2">
          <input
            name="email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            placeholder={tGlobal('Email label', 'Email')}
            className="input text-body-1 w-full"
          />
          <input
            name="password"
            type="password"
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder={tGlobal('Password label', 'Password')}
            className="input text-body-1 w-full"
          />
        </div>
        {error && <p className="mb-7 font-nimbus-sans-d text-[14px] text-blue">{error}</p>}
        <div className="mx-[2px] mb-9 flex flex-row justify-start">
          <SiteLink
            href="/account/forgot-password/"
            className="text-body-2 font-nimbus-sans-d leading-[14px] text-blue underline"
          >
            {t('Forgot password link text', 'Forgot password')}
          </SiteLink>
        </div>
        <button type={loading ? 'button' : 'submit'} className="button mb-6 w-full">
          {loading
            ? tGlobal('Button loading text', 'Loading...')
            : t('Login button text', 'Log in')}
        </button>
        <div className="mb-9 flex flex-col gap-2">
          <Button
            onClick={() => signIn('google')}
            className="[&>div]:flex [&>div]:gap-2.5"
            type="Button (Outline)"
          >
            {loading ? (
              <Spinner className="h-8" />
            ) : (
              <>
                <Icon className="h-4 w-4" type="google" />
                <span>{t('Continue With Google')}</span>
              </>
            )}
          </Button>
          {/* Temporarily Comment Facebook */}
          {/* <Button
            onClick={() => signIn('facebook')}
            className="[&>div]:flex [&>div]:gap-2.5"
            type="Button (Outline)"
          >
            {loading ? (
              tGlobal('Button loading text', 'Loading...')
            ) : (
              <>
                <Icon className="h-4 w-4" type="facebook" />
                <span> {t('Continue With Facebook')}</span>
              </>
            )}
          </Button> */}
        </div>
        <p className="text-body-2 flex gap-2 text-blue">
          {t('No Account Text')}{' '}
          <a href={`${accountSlug}/register/`} className="underline">
            {t('Create Account Text')}
          </a>
        </p>
      </form>
    </AccountWrapper>
  );
};

export default Login;
